'use client';
/**
 * components/SyncPreferences.tsx — les préférences suivent la personne.
 *
 * POURQUOI (28/08). Le thème et la main dominante vivaient dans `localStorage` :
 * changer de téléphone remettait l'interface à zéro, et un gaucher retrouvait
 * ses boutons du mauvais côté. Elles vivent désormais dans `users.preferences`.
 *
 * LE NAVIGATEUR GARDE UNE COPIE, ET C'EST VOULU. Le script du layout lit
 * `localStorage` pour poser `data-theme` et `data-hand` sur <html> AVANT
 * l'hydratation : sans lui, chaque chargement montrerait un écran clair qui
 * bascule en sombre une fraction de seconde plus tard. Mais la base décide — la
 * copie locale n'est qu'un miroir, jamais une seconde vérité.
 *
 * DEUX SENS, ET LE BON ARBITRE À CHAQUE FOIS :
 *   — au montage, la base gagne : c'est elle qui porte ce que la personne a
 *     choisi, fût-ce depuis un autre appareil ;
 *   — ensuite, tout changement local part vers la base.
 */
import { useEffect, useRef } from 'react';
import { trpc } from '@/lib/trpc/client';
import { useCompte } from '@/lib/auth/client';
import { themeStore, CLE_THEME, type LabTheme } from '@/components/theme';
import { applyHand, HAND_KEY, readHand, type Hand } from '@/lib/prefs';

export default function SyncPreferences() {
  const { isSignedIn } = useCompte();
  const me = trpc.users.me.useQuery(undefined, {
    enabled: !!isSignedIn, retry: 1, staleTime: 5 * 60_000,
  });
  const setPrefs = trpc.users.setPreferences.useMutation();

  /** La base a-t-elle déjà eu le dernier mot sur cet écran ? */
  const applique = useRef(false);

  // ── Descente : ce que dit la base s'applique à l'écran ────────────────────
  useEffect(() => {
    if (applique.current || !me.data) return;
    applique.current = true;

    const p = (me.data.user.preferences ?? {}) as { theme?: string; hand?: string };

    if (p.theme === 'light' || p.theme === 'dark') {
      themeStore.ecrire(p.theme as LabTheme);
    }
    if (p.hand === 'left' || p.hand === 'right') {
      applyHand(p.hand as Hand);
      try { localStorage.setItem(HAND_KEY, p.hand); } catch { /* mode privé */ }
    }

    // Premier passage d'un compte qui n'a encore rien en base : on y verse ce
    // que cet appareil sait déjà, plutôt que de perdre son réglage.
    if (p.theme === undefined || p.hand === undefined) {
      setPrefs.mutate({
        ...(p.theme === undefined ? { theme: themeStore.lire() } : {}),
        ...(p.hand  === undefined ? { hand: readHand() } : {}),
      });
    }
  }, [me.data, setPrefs]);

  // ── Montée : un changement fait ici part vers la base ─────────────────────
  useEffect(() => {
    if (!isSignedIn) return;

    // Le thème s'annonce par son propre magasin ; la main, par l'attribut du
    // DOM que `applyHand` écrit — c'est le seul signal commun aux deux écrans
    // qui la règlent.
    const desabonne = themeStore.subscribe(() => {
      if (!applique.current) return;
      setPrefs.mutate({ theme: themeStore.lire() });
    });

    const observateur = new MutationObserver(() => {
      if (!applique.current) return;
      const h = document.documentElement.dataset.hand;
      if (h === 'left' || h === 'right') setPrefs.mutate({ hand: h });
    });
    observateur.observe(document.documentElement, {
      attributes: true, attributeFilter: ['data-hand'],
    });

    return () => { desabonne(); observateur.disconnect(); };
  }, [isSignedIn, setPrefs]);

  // Rien à peindre : ce composant ne fait que tenir les deux mondes d'accord.
  return null;
}

/** Rappel des clés locales, pour qui chercherait d'où vient le premier rendu. */
export const CLES_LOCALES = [CLE_THEME, HAND_KEY];
